import { ChangeEvent, FunctionComponent, useEffect, useState } from 'react';
import {
    Box,
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    Stack,
    TextField,
    Typography,
    TypographyProps,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import {
    createEmptyHospitalityUserWithCredentials,
    Hospitality,
    HospitalityUserWithCredentials,
} from '@/models/hospitality';
import {
    usePostHospitalityMutation,
    usePutHospitalityMutation,
} from '@/api/v2/hospitality';
import { useNotification } from '@starjack/features/notifications';
import { useAuthentication } from '@/hooks/useAuthentication';
import isEqual from 'lodash/isEqual';
import { useDirtyGuard } from '@starjack/shared-components';
import { BoldLabel } from '@starjack/shared-components';

const typographySyling: TypographyProps['sx'] = {
    width: '100%',
};

type NewHospitalityContentProps = {
    onClose: () => void;
    editHospitality: HospitalityUserWithCredentials | null;
};

export const HospitalityDrawer: FunctionComponent<
    NewHospitalityContentProps
> = ({ onClose, editHospitality }) => {
    const mode = editHospitality ? 'edit' : 'new';
    const {
        liftOperatorData: { liftOperatorId },
    } = useAuthentication();
    const [hospitality, setHospitality] =
        useState<HospitalityUserWithCredentials>(
            editHospitality ||
                createEmptyHospitalityUserWithCredentials(liftOperatorId)
        );
    const [showInputError, setShowInputError] = useState(false);
    const [resetPassword, setResetPassword] = useState(false);
    useDirtyGuard(
        !isEqual(
            hospitality,
            editHospitality ||
                createEmptyHospitalityUserWithCredentials(liftOperatorId)
        ) || resetPassword
    );

    const [postHospitality, { isLoading: postHospitalityLoading }] =
        usePostHospitalityMutation();
    const [putHospitality, { isLoading: putHospitalityLoading }] =
        usePutHospitalityMutation();
    const { addNotification } = useNotification();
    const [showPasswordDialog, setShowPasswordDialog] = useState<string>(null);

    const onSave = () => {
        if (!hospitality.name) {
            setShowInputError(true);
            return;
        }

        const action = mode === 'edit' ? putHospitality : postHospitality;
        const args =
            mode === 'edit' ? { resetPassword, ...hospitality } : hospitality;

        action(args)
            .unwrap()
            .then((newHospitality: HospitalityUserWithCredentials) => {
                addNotification({
                    type: 'success',
                    message: 'Hospitality erfolgreich gespeichert',
                });

                if (newHospitality.password) {
                    setShowPasswordDialog(newHospitality.password);
                    setHospitality(newHospitality);
                } else {
                    onClose();
                }
            })
            .catch(() => {
                addNotification({
                    type: 'error',
                    message:
                        'Fehler beim Speichern. Bitte später noch einmal versuchen',
                });
            });
    };

    const onInputChange =
        (key: keyof Hospitality) => (e: ChangeEvent<HTMLInputElement>) => {
            const { value, checked } = e.target;

            const nextValue = key === 'enabled' ? checked : value;

            if (key === 'name' && nextValue) {
                setShowInputError(false);
            }

            setHospitality((prevState) => ({
                ...prevState,
                [key]: nextValue,
            }));
        };

    useEffect(() => {
        setHospitality(
            editHospitality ||
                createEmptyHospitalityUserWithCredentials(liftOperatorId)
        );
    }, [editHospitality]);

    return (
        <Box sx={{ width: '440px' }}>
            <Box>
                <Typography
                    variant='h6'
                    sx={{
                        bgcolor: 'primary.dark',
                        color: '#fff',
                        p: 4,
                    }}
                >
                    Neue Verkaufsstelle für hospitality anlegen
                </Typography>
            </Box>

            <Stack
                sx={{
                    p: 4,
                }}
                spacing={3}
            >
                <TextField
                    sx={{ ...typographySyling }}
                    onChange={onInputChange('name')}
                    value={hospitality.name}
                    label='Name'
                    error={showInputError}
                    helperText={
                        showInputError
                            ? 'Bitte einen Benutzernamen angeben'
                            : ''
                    }
                    required
                />
                <TextField
                    sx={{ ...typographySyling }}
                    onChange={onInputChange('dtaPointOfSaleName')}
                    value={hospitality.dtaPointOfSaleName}
                    label='DTA Verkaufsstelle'
                />
                <TextField
                    sx={{ ...typographySyling }}
                    value={hospitality.beiUsername}
                    onChange={onInputChange('beiUsername')}
                    label='BEI Benutzername'
                />
                <TextField
                    sx={{ ...typographySyling }}
                    label='BEI Passwort'
                    value={hospitality.beiPassword}
                    onChange={onInputChange('beiPassword')}
                />

                {!!editHospitality && (
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={resetPassword}
                                onChange={() => {
                                    setResetPassword((prevState) => !prevState);
                                }}
                            />
                        }
                        label='Neues Passwort'
                    />
                )}

                <FormControlLabel
                    control={
                        <Checkbox
                            checked={hospitality.enabled}
                            onChange={onInputChange('enabled')}
                        />
                    }
                    label='Aktiviert'
                />

                <LoadingButton
                    sx={{ ...typographySyling }}
                    variant='contained'
                    onClick={onSave}
                    loading={postHospitalityLoading || putHospitalityLoading}
                >
                    Speichern
                </LoadingButton>

                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        width: '100%',
                    }}
                >
                    <Button
                        sx={{
                            width: 200,
                            mx: 'auto',
                        }}
                        onClick={onClose}
                        variant='outlined'
                    >
                        Abbrechen
                    </Button>
                </Box>
            </Stack>

            <Dialog
                onClose={() => {
                    setShowPasswordDialog(null);
                    onClose();
                }}
                open={!!showPasswordDialog}
            >
                <DialogTitle>B2B-Passwort</DialogTitle>
                <DialogContent>
                    Das Passwort für die angelegte Hospitality-Verkaufsstelle
                    lautet: <BoldLabel>{hospitality.password}</BoldLabel>. Bitte
                    an einem sicheren Ort speichern. Das Passwort kann später
                    nicht mehr aufgerufen werden.
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => {
                            setShowPasswordDialog(null);
                            onClose();
                        }}
                    >
                        Schließen
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};
